import React, { useState, useEffect } from "react";
import { Row, Col, Spin, Card, Empty } from "antd";
import { Line, Pie, Bar } from "react-chartjs-2";
import moment from "moment";
import axios from "axios";
import { Link,useParams } from "react-router-dom";
import { useNotification } from "../../../context/NotificationContext";
import SurveySelect from "../components/SurveySelect";

const ReviewDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [filters, setFilters] = useState({ surveyId: null });
  const [loading, setLoading] = useState(false);
  const { notify } = useNotification();

  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:3000/api/pos";
  const token = localStorage.getItem("token");

  const authHeaders = () => ({
    headers: { Authorization: token ? `Bearer ${token}` : "" },
  });

  // Fetch surveys
  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/surveys`, authHeaders());
      const data = Array.isArray(res.data) ? res.data : [];
      const active = data.filter((s) => s.status === "Active");
      setSurveys(active);

      if (active.length > 0) {
        setFilters((prev) => ({ ...prev, surveyId: active[0].surveyid }));
        fetchSummary(active[0].surveyid);
      }
    } catch (err) {
      console.error("Error fetching surveys:", err);
      notify("error", "Failed to load surveys.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary for selected survey
  const fetchSummary = async (surveyId) => {
    if (!surveyId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiUrl}/feedback/surveys/${surveyId}/analytics/summary`,
        authHeaders()
      );
      setSummary(res.data);
    } catch (err) {
      console.error("Error fetching summary:", err);
      notify("error", "Failed to load survey summary.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  // Charts
  const sentimentChart = {
    labels: ["Positive", "Neutral", "Negative"],
    datasets: [
      {
        data: [
          summary?.positivePct || 0,
          100 - (summary?.positivePct || 0) - (summary?.negativePct || 0),
          summary?.negativePct || 0,
        ],
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
      },
    ],
  };

  const npsTrendChart = {
    labels: summary?.npsTrend?.map((t) => t.date) || [],
    datasets: [
      {
        label: "NPS (%)",
        data: summary?.npsTrend?.map((t) => t.value) || [],
        borderColor: "#faad14",
        backgroundColor: "rgba(250,173,20,0.2)",
        fill: true,
      },
    ],
  };

  const ratingTrendChart = {
    labels: summary?.ratingTrend?.map((t) =>
      moment(t.date).format("MMM DD")
    ) || [],
    datasets: [
      {
        label: "Avg Rating",
        data: summary?.ratingTrend?.map((t) => t.value) || [],
        borderColor: "#1890ff",
        backgroundColor: "rgba(24,144,255,0.2)",
        tension: 0.3,
      },
    ],
  };


  const categoryLabels = summary?.categoryAverages
    ? Object.keys(summary.categoryAverages)
    : [];

  const categoryValues = summary?.categoryAverages
    ? Object.values(summary.categoryAverages).map((v) => Number(v))
    : [];

  const categoryAveragesChart = {
    labels: categoryLabels,
    datasets: [
      {
        label: "Avg Rating (out of 5)",
        data: categoryValues,
        backgroundColor: [
          "#10b981",
          "#3b82f6",
          "#f59e0b",
          "#d946ef",
          "#ef4444",
          "#16a34a",
          "#6366f1",
          "#f97316",
          "#a855f7",
          "#dc2626", // add more if many categories
        ],
      },
    ],
  };


  // KPI card helper
  const renderKpiCard = (title, value, color, link) => (
    <Card
      hoverable
      style={{ borderRadius: 12, background: color, color: "#fff" }}
    >
      <h4 style={{ marginBottom: 8, color: "#fff" }}>{title}</h4>
      <h2 style={{ fontWeight: "bold", fontSize: 24, color: "#fff" }}>
        {value}
      </h2>
      {link && (
        <div style={{ marginTop: 8 }}>
          <Link to={link} style={{ color: "#fff", textDecoration: "underline" }}>
            View Feedback →
          </Link>
        </div>
      )}
    </Card>
  );
const { source } = useParams();  

  // Conditional class based on source
  const wrapperClass = source && source === 'table-flow' ? 'page-wrapper' : 'page-wrapper-full';
  return (
<div className={wrapperClass}>
        <div className="content container-fluid">
        <Spin spinning={loading}>
          {/* Header */}
          <div className="page-header page-top-icons">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <h2 className="mb-0 fw-bold text-dark">FEEDBACK ANALYTICS</h2>
              <div style={{ minWidth: "300px" }}>
                <SurveySelect
                  value={filters.surveyId}
                  onChange={(option) => {
                    const surveyId = option?.value || null;
                    setFilters((prev) => ({ ...prev, surveyId }));
                    fetchSummary(surveyId);
                  }}
                  notify={notify}
                />
              </div>
            </div>
          </div>

          {/* If no responses */}
          {summary?.totalReviews === 0 ? (
            <div style={{ padding: "80px 0", textAlign: "center" }}>
              <Empty
                description={
                  <span>
                    No responses yet. Share your survey to start collecting
                    feedback.
                  </span>
                }
              />
            </div>
          ) : (
            <>
              {/* KPIs */}
              <Row gutter={16} className="mb-4">
                <Col span={6}>
                  {renderKpiCard(
                    "Total Reviews",
                    summary?.totalReviews,
                    "linear-gradient(135deg, #3b82f6, #60a5fa)",
                    `/feedback/feedback-responses?survey=${filters.surveyId}`
                  )}
                </Col>
                <Col span={6}>
                  {renderKpiCard(
                    "Average Rating",
                    `${summary?.averageRating || 0} ⭐`,
                    "linear-gradient(135deg, #52c41a, #73d13d)"
                  )}
                </Col>
                <Col span={6}>
                  {renderKpiCard(
                    "NPS",
                    `${summary?.nps || 0}%`,
                    "linear-gradient(135deg, #faad14, #fadb14)"
                  )}
                </Col>
                <Col span={6}>
                  {renderKpiCard(
                    "CSAT",
                    `${summary?.csat || 0}%`,
                    "linear-gradient(135deg, #722ed1, #9254de)"
                  )}
                </Col>
              </Row>

              <Row gutter={16} className="mb-4">
                <Col span={6}>
                  {renderKpiCard(
                    "Positive Reviews",
                    `${summary?.positivePct || 0}%`,
                    "linear-gradient(135deg, #10b981, #34d399)"
                  )}
                </Col>
                <Col span={6}>
                  {renderKpiCard(
                    "Negative Reviews",
                    `${summary?.negativePct || 0}%`,
                    "linear-gradient(135deg, #ef4444, #f87171)"
                  )}
                </Col>
                <Col span={6}>
                  {renderKpiCard(
                    "Promoters",
                    summary?.promoters || 0,
                    "linear-gradient(135deg, #1890ff, #40a9ff)"
                  )}
                </Col>
                <Col span={6}>
                  {renderKpiCard(
                    "Detractors",
                    summary?.detractors || 0,
                    "linear-gradient(135deg, #d946ef, #e879f9)"
                  )}
                </Col>
              </Row>

              {/* Charts */}
<Row gutter={16} className="mb-4" align="stretch">
  <Col span={12}>
    <Card title="Sentiment Breakdown" style={{ height: "50%" }}>
      
      <Pie data={sentimentChart} />
    </Card>
  </Col>
  <Col span={12}>
    <Card title="NPS Trend" style={{ height: "50%" }}>
      <Line data={npsTrendChart} />
    </Card>
  </Col>
</Row>

              <Row gutter={16} className="mb-4">
                <Col span={12}>
                  <Card title="Average Rating Trend">
                    <Line data={ratingTrendChart} />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Category Averages">
                    <Bar data={categoryAveragesChart} />
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default ReviewDashboard;
